import {cn} from '@cohort/shared-frontend/utils/classNames';
import type {ChangeEvent, ChangeEventHandler, FocusEventHandler, LegacyRef} from 'react';
import React, {forwardRef, useCallback} from 'react';

export type InputType = 'text' | 'textArea' | 'number' | 'checkbox';
export type FlexAlignment = 'flex-col' | 'flex-row';

interface InputWithLabelProps {
  type?: InputType;
  label?: string;
  name?: string;
  testId?: string;
  placeholder?: string;
  disabled?: boolean;
  prefixAddon?: string;
  suffixAddon?: string;
  trailingAddon?: JSX.Element;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  defaultValue?: string;
  children?: React.ReactNode;
  tooltip?: string;
  error?: string;
  flexAlignment?: FlexAlignment;
  maxLength?: number;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  autoFocus?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  leadingIcon?: JSX.Element;
  trailingIcon?: JSX.Element;
  subtitle?: JSX.Element | string;
  checked?: boolean;
}

export const InputWithLabel = forwardRef(
  (
    {
      className,
      inputClassName,
      label,
      disabled,
      flexAlignment,
      maxLength,
      type,
      name,
      testId,
      error,
      onChange,
      tooltip,
      required,
      placeholder,
      defaultValue,
      trailingAddon,
      suffixAddon,
      prefixAddon,
      leadingIcon,
      trailingIcon,
      subtitle,
      checked,
      ...props
    }: InputWithLabelProps,
    ref?: LegacyRef<HTMLInputElement> | LegacyRef<HTMLTextAreaElement>
  ) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange?.(event),
      [onChange]
    );

    return (
      <div
        className={cn(
          className,
          type === 'checkbox' && 'flex flex-row-reverse items-center justify-end'
        )}
      >
        {label !== undefined && (
          <label className="block text-sm font-medium text-slate-700" data-testid={`${name}-label`}>
            {label}
          </label>
        )}

        {(type === 'text' || type === 'number') && (
          <div className="relative mt-2 flex">
            <div className="flex-auto">
              <div
                className={`flex ${
                  flexAlignment !== undefined
                    ? flexAlignment
                    : prefixAddon !== undefined
                      ? 'flex-row'
                      : 'flex-col'
                } flex-auto`}
              >
                {prefixAddon !== undefined && (
                  <span className="inline-flex items-center rounded-l-md border border-r-0 bg-slate-50 px-3 text-slate-500 sm:text-sm">
                    {prefixAddon}
                  </span>
                )}
                <div className={`relative ${inputClassName}`}>
                  {leadingIcon !== undefined && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      {leadingIcon}
                    </div>
                  )}
                  <input
                    disabled={disabled}
                    ref={ref as LegacyRef<HTMLInputElement>}
                    type={type}
                    data-testid={testId}
                    className={cn(
                      'block w-full border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm',
                      prefixAddon === undefined && 'rounded-l-md',
                      suffixAddon === undefined && 'rounded-r-md',
                      leadingIcon !== undefined && 'pl-10'
                    )}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    maxLength={maxLength}
                    onChange={handleChange}
                    required={required}
                    {...props}
                    name={name}
                  />
                  {trailingIcon !== undefined && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      {trailingIcon}
                    </div>
                  )}
                  {trailingAddon !== undefined && trailingAddon}
                </div>
                {suffixAddon !== undefined && (
                  <span className="inline-flex items-center rounded-r-md border border-l-0 bg-slate-50 px-3 text-slate-500 sm:text-sm">
                    {suffixAddon}
                  </span>
                )}
              </div>
            </div>
            {tooltip !== undefined && <div className="mt-1 text-sm text-slate-600">{tooltip}</div>}
          </div>
        )}

        {type === 'textArea' && (
          <div className="mt-2">
            <textarea
              disabled={disabled}
              ref={ref as LegacyRef<HTMLTextAreaElement>}
              name={name}
              data-testid={testId}
              rows={5}
              defaultValue={defaultValue}
              placeholder={placeholder}
              maxLength={maxLength}
              required={required}
              onChange={handleChange}
              className={`block w-full rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${inputClassName}`}
              {...props}
            />
          </div>
        )}

        {type === 'checkbox' && (
          <div className="mt-2">
            <input
              disabled={disabled}
              ref={ref as LegacyRef<HTMLInputElement>}
              type="checkbox"
              name={name}
              defaultChecked={checked}
              data-testid={testId}
              onChange={handleChange}
              className={`mr-2 block h-4 w-4 rounded-md shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${inputClassName}`}
              {...props}
            />
          </div>
        )}

        {subtitle !== undefined && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
        {error !== undefined && (
          <p
            className="mt-2 text-sm text-red-500"
            data-testid={testId !== undefined ? `${testId}-error` : undefined}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);
